import { createTheme } from '@mui/material';

// Augment the palette to include an tertiary color
declare module '@mui/material/styles' {
  interface TypeText {
    tertiary: string; // Add the new tertiary color to TypeText
  }

  interface Palette {
    tertiary: Palette['primary'];
    solitude: Palette['primary'];
    paleBlue: Palette['primary'];
    ghostWhite: Palette['primary'];
    text: TypeText; // Ensure the Palette's text uses the extended TypeText interface
  }

  interface PaletteOptions {
    tertiary?: PaletteOptions['primary'];
    solitude?: PaletteOptions['primary'];
    paleBlue?: PaletteOptions['primary'];
    ghostWhite?: PaletteOptions['primary'];
    text?: Partial<TypeText>;
    bg?: PaletteOptions['primary'];
  }
}

// Update the Button's color options to include an tertiary option
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertiary: true;
  }
}

declare module '@mui/material/Switch' {
  interface SwitchPropsColorOverrides {
    tertiary: true;
  }
}

declare module '@mui/material/Radio' {
  interface RadioPropsColorOverrides {
    tertiary: true;
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#3538cd',
      light: '#c0c2ff',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#fe4d01',
      light: '#fe4d0133',
      contrastText: '#ffffff',
    },
    tertiary: {
      main: '#7625dc',
      dark: '#4a168c',
      light: '#d888ff',
      contrastText: '#ffffff',
    },
    error: {
      main: '#d92d20',
    },
    success: {
      main: '#03820F',
    },
    info: {
      main: '#6063f2',
      contrastText: '#ffffff',
    },
    solitude: {
      main: '#f2f4f7',
    },
    paleBlue: {
      main: '#d9daff',
    },
    ghostWhite: {
      main: '#f6f6ff',
    },
    text: {
      primary: '#101828',
      secondary: '#475467',
      tertiary: '#344054',
    },
    grey: {
      50: '#fcfcfd',
      900: '#1d2939',
    },
    bg: {
      main: `linear-gradient(to bottom, #FFF1D5, #F3F3FF)`,
    },
  },

  typography: {
    htmlFontSize: 16,
    fontFamily: "'Lexend', sans-serif",
    fontSize: 18,
    h4: {
      fontSize: '2rem',
      lineHeight: '2.25rem',
      fontWeight: 600,
      letterSpacing: '-0.0075em',
    },
    h5: {
      fontSize: '1.75rem',
      lineHeight: '2rem',
      fontWeight: 600,
      letterSpacing: '-0.0075em',
    },
    h6: {
      fontSize: '1.5rem',
      lineHeight: '1.75rem',
      fontWeight: 600,
      letterSpacing: '-0.0075em',
    },
    subtitle1: {
      fontSize: '1.5rem',
      lineHeight: '1.75rem',
      fontWeight: 500,
      letterSpacing: '-0.0075em',
    },
    subtitle2: {
      fontSize: '1.25rem',
      lineHeight: '1.5rem',
      fontWeight: 'inherit',
      letterSpacing: '-0.0075em',
    },
    body1: {
      fontSize: '1.125rem',
      lineHeight: '1.375rem',
      fontWeight: 'inherit',
      letterSpacing: '-0.0075em',
    },
    body2: {
      fontSize: '1rem',
      lineHeight: '1.25rem',
      fontWeight: 400,
      letterSpacing: '-0.0075em',
    },
    caption: {
      fontSize: '0.875rem',
      lineHeight: '1.125rem',
      fontWeight: 300,
      letterSpacing: '-0.0075em',
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          fontWeight: 600,
          lineHeight: '24px',
          textTransform: 'inherit',
          letterSpacing: '-0.0075em',
          ...(ownerState.variant === 'text' && {
            fontWeight: 400,
            lineHeight: '22px',
            color: theme.palette.info.main,
            textDecoration: 'underline',
            padding: 0,
            borderRadius: 0,
          }),
          ...((ownerState.variant === 'contained' ||
            ownerState.variant === 'outlined') && {
            padding: '20px 24px',
            borderRadius: '50rem',
          }),
          ...(ownerState.variant === 'outlined' &&
            ownerState.color === 'primary' && {
              borderColor: theme.palette.primary.main,
            }),
          ...(ownerState.size === 'small' && {
            fontSize: '18px',
            fontWeight: 400,
            padding: '16px 24px',
          }),
          ...(ownerState.size === 'large' && {
            fontSize: '24px',
            lineHeight: '32px',
            fontWeight: 600,
            padding: '24px',
          }),
        }),
        iconSizeSmall: {
          '& > *:first-child': {
            fontSize: 24,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: () => ({
          borderColor: theme.palette.primary.main,
        }),
        root: () => ({
          borderRadius: '24px',
          backgroundColor: theme.palette.common.white,
          lineHeight: '24px',
        }),
        input: () => ({
          height: 'auto',
          lineHeight: '24px',
          color: theme.palette.text.secondary,
          padding: '20px 16px',
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: () => ({
          lineHeight: '24px',
          top: '4px',
          [`&.Mui-focused, &.MuiInputLabel-shrink`]: {
            transform: 'translate(14px, -12px) scale(0.75)',
          },
        }),
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: () => ({
          [`.MuiBottomNavigationAction-label`]: {
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 400,
            letterSpacing: '-0.0075em',
          },
        }),
      },
    },
    MuiMobileStepper: {
      styleOverrides: {
        root: () => ({
          backgroundColor: 'transparent',
          padding: 0,
        }),
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: () => ({
          [`&.MuiRadioGroup-root .MuiFormControlLabel-root`]: {
            gap: 12,
            margin: 0,
          },
          [`&.button-radio-group`]: {
            [`&.MuiRadioGroup-root`]: {
              gap: 16,
              marginBottom: 16,
            },
            [`&.MuiRadioGroup-root .MuiFormControlLabel-root`]: {
              height: 80,
              backgroundColor: theme.palette.common.white,
              justifyContent: 'center',
              padding: '24px 28px',
              margin: 0,
              borderRadius: 50,
              transition: 'background-color 0.15ms ease-in',
            },
            [`&.MuiRadioGroup-root .MuiFormControlLabel-root:has(.Mui-checked)`]:
              {
                backgroundColor: theme.palette.primary.light,
                transition: 'background-color 0.15ms ease-in',
              },
            [`&.MuiRadioGroup-root .MuiFormControlLabel-root .MuiRadio-root`]: {
              display: 'none',
            },
          },
        }),
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: () => ({
          padding: 0,
        }),
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: () => ({
          width: 48,
          height: 24,
          padding: 0,
          borderRadius: 50,
          [`.MuiSwitch-switchBase`]: {
            padding: 3,
            [`&.Mui-checked`]: {
              color: theme.palette.common.white,
              transform: 'translateX(24px)',
              [`+.MuiSwitch-track`]: {
                opacity: 1,
              },
            },
          },
          [`.MuiSwitch-thumb`]: {
            width: 18,
            height: 18,
            boxShadow: '0 0 4px 0 #00000014',
          },
          [`.MuiSwitch-track`]: {
            backgroundColor: '#475467',
          },
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: () => ({
          backgroundColor: '#0000001A',
          backdropFilter: 'blur(2px)',
        }),
      },
    },
  },
});

export default theme;
